import { ContentDrawer, PlusIcon, DeleteIcon } from '@halo/ui-chat-react';
import {
  Stack,
  Text,
  Field,
  Textarea,
  Select,
  Box,
  createListCollection,
  Center,
  Button,
  Flex,
  FileUpload,
  Image,
  HStack,
  Spinner,
} from '@chakra-ui/react';
import { memo, useMemo, useState, useCallback, useEffect } from 'react';
import { toaster } from '@halo/ui-chat-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@halo/chat-sdk-react';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { useActionsStore } from '@/store/actions';

export interface ReportCardDrawerProps {
  closeAllClick?: () => void;
  from: 'user' | 'group';
  id: string;
}

const ReportCardDrawer = ({ closeAllClick, from, id }: ReportCardDrawerProps) => {
  const { t } = useTranslation();
  const { client } = useAppStore();
  const { reportIsOpen, setReportIsOpen } = useActionsStore(
    useShallowEnhance(['reportIsOpen', 'setReportIsOpen'])
  );
  const {
    handleSubmit,
    register,
    getValues,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<{ type: string; desc: string; img: string[] }>({
    mode: 'onChange',
  });
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const maxImageCount = 3;
  const onSubmit = async (data: { type: string; desc: string; img: string[] }) => {
    try {
      setLoading(true);
      const res = await client?.Api.common.postReport({
        object: from === 'user' ? 1 : 2,
        id,
        ...data,
        desc: data.desc.trim(),
        type: Number(data.type),
      });

      if (res?.code === 1) {
        setReportIsOpen(false);
        toaster.success({
          title: t('tips.submittedSuccess'),
        });
      } else if (res?.code === 16001) {
        toaster.error({
          title: t('tips.alreadyReported'),
        });
      } else if (res?.code === 16002) {
        toaster.error({
          title: t('tips.reportLimitReached'),
        });
      } else {
        toaster.error({
          title: t('tips.submittedFailed'),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const typeValue = watch('type');
  const descriptionValue = watch('desc') || '';
  const images = watch('img') || [];
  const imageCount = images.length;

  const reportType = createListCollection({
    items: [
      { label: t('chatSettings.reportSpam'), value: '1' },
      { label: t('chatSettings.reportIllegal'), value: '2' },
      { label: t('chatSettings.reportScam'), value: '3' },
      { label: t('chatSettings.reportInappropriate'), value: '4' },
      { label: t('chatSettings.reportOther'), value: '5' },
    ],
  });

  const readFileAsDataURL = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const handleFileChange = useCallback(
    async (details: { acceptedFiles: File[] }) => {
      const files = details.acceptedFiles;
      if (files.length === 0 || !client) return;

      const imageFiles = files.filter((file) => file.type.startsWith('image/'));
      if (imageFiles.length === 0) return;

      const currentImages = getValues('img') || [];
      const remainingSlots = maxImageCount - currentImages.length;
      const filesToAdd = imageFiles.slice(0, remainingSlots);

      try {
        setIsUploading(true);
        const newPreviews = await Promise.all(filesToAdd.map((file) => readFileAsDataURL(file)));

        const newAccessUrls = await Promise.all(
          filesToAdd.map((file) =>
            client.Api.common.getUploadAvatar({
              file,
              type: file.name.split('.').pop(),
              category: 'avatar',
            })
          )
        );

        const newImagePreviews = [...imagePreviews, ...newPreviews];
        const newImages = [...currentImages, ...newAccessUrls];

        setImagePreviews(newImagePreviews);
        setValue('img', newImages, { shouldValidate: true });
        setUploadKey((prev) => prev + 1);
      } catch (error) {
        console.error('Error uploading files:', error);
      } finally {
        setIsUploading(false);
      }
    },
    [client, getValues, setValue, imagePreviews, readFileAsDataURL]
  );

  const removeImage = useCallback(
    (index: number) => {
      const currentImages = getValues('img') || [];
      const newImages = currentImages.filter((_, i) => i !== index);
      const newPreviews = imagePreviews.filter((_, i) => i !== index);
      setImagePreviews(newPreviews);
      setValue('img', newImages, { shouldValidate: true });
    },
    [getValues, setValue, imagePreviews]
  );

  const disabled = useMemo(() => {
    return (
      !typeValue ||
      !descriptionValue.trim() ||
      descriptionValue.length < 20 ||
      loading ||
      isUploading
    );
  }, [typeValue, descriptionValue, loading, isUploading]);

  useEffect(() => {
    if (reportIsOpen) {
      reset();
      setImagePreviews([]);
      setLoading(false);
      setIsUploading(false);
    }
  }, [reportIsOpen, reset]);
  return (
    <ContentDrawer
      title={t('chatSettings.reportCard')}
      open={reportIsOpen}
      onOpenChange={(open) => {
        setReportIsOpen(open);
      }}
      closeAllClick={closeAllClick}
    >
      <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
        <Flex direction="column" justify="space-between" h="full">
          <Stack gap="2" align="flex-start">
            <Stack gap="0" align="flex-start" w="full">
              <Field.Root invalid={!!errors.type} required>
                <Field.Label
                  pt="2.5"
                  pb="1.5"
                  fontSize="xs"
                  color="fg.muted"
                  fontWeight="medium"
                  lineHeight="normal"
                >
                  <Field.RequiredIndicator />
                  {t('chatSettings.reportType')}
                </Field.Label>
                <Select.Root
                  bg="bg"
                  rounded="lg"
                  collection={reportType}
                  w="full"
                  size="lg"
                  {...register('type', { required: true })}
                  value={typeValue ? [typeValue] : undefined}
                  onValueChange={({ value }) => {
                    const val = value?.[0] as string;
                    setValue('type', val, { shouldValidate: true });
                  }}
                >
                  <Select.HiddenSelect />
                  <Select.Control>
                    <Select.Trigger border="none" cursor="pointer" fontWeight="medium">
                      <Select.ValueText
                        fontSize="sm"
                        placeholder={t('chatSettings.selectReportType')}
                      />
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content p="1" rounded="xl" border="none" fontWeight="semibold">
                      {reportType.items.map((item) => (
                        <Select.Item
                          item={item}
                          key={item.value}
                          px="2"
                          py="3"
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          _hover={{ bg: 'grey.10', borderRadius: 'sm' }}
                        >
                          <Box fontSize="sm" lineHeight="100%">
                            {item.label}
                          </Box>
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
                <Field.ErrorText>{errors?.type?.message as string}</Field.ErrorText>
              </Field.Root>
            </Stack>
            <Stack gap="0" align="flex-start" w="full">
              <Field.Root invalid={!!errors.desc}>
                <Field.Label
                  pt="2.5"
                  pb="1.5"
                  fontSize="xs"
                  color="fg.muted"
                  fontWeight="medium"
                  lineHeight="normal"
                >
                  <Text color="red.100">*</Text>
                  {t('chatSettings.description')}
                </Field.Label>
                <Textarea
                  background="bg"
                  rows={6}
                  maxLength={1000}
                  borderColor="border.input"
                  rounded="lg"
                  {...register('desc', {
                    validate: (value) => {
                      if (value.length < 20) {
                        return t('chatSettings.descriptionMinLength');
                      }
                      return true;
                    },
                  })}
                  placeholder={t('chatSettings.descriptionPlaceholder')}
                  _placeholder={{ color: 'fg.subtle', fontSize: 'sm', fontWeight: 'medium' }}
                  autoComplete="off"
                />
                <Flex justify="space-between" w="full" align="flex-start">
                  <Field.ErrorText flex="1">{errors?.desc?.message as string}</Field.ErrorText>
                  <Field.HelperText as="div" ms="auto" flexShrink={0}>
                    {descriptionValue.length}/1000
                  </Field.HelperText>
                </Flex>
              </Field.Root>
            </Stack>
            <Stack gap="0" align="flex-start" w="full">
              <Text
                py="2.5"
                fontSize="xs"
                color="fg.muted"
                fontWeight="medium"
                lineHeight="normal"
              >
                {t('chatSettings.addImage')} (
                <Text as="span" color="fg">
                  {imageCount}
                </Text>
                /{maxImageCount})
              </Text>
              <Field.Root invalid={!!errors.img}>
                <HStack gap="2.5" align="flex-start" flexWrap="nowrap">
                  {imagePreviews.map((preview, index) => (
                    <Box
                      key={index}
                      position="relative"
                      w="80px"
                      h="80px"
                      flexShrink={0}
                      borderRadius="md"
                      overflow="hidden"
                      bg="bg.subtle"
                      border="1px solid"
                      borderColor="border.subtle"
                      cursor="pointer"
                      css={{
                        '&:hover .delete-overlay': {
                          display: 'flex',
                        },
                      }}
                    >
                      <Image
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        w="full"
                        h="full"
                        objectFit="cover"
                      />
                      <Box
                        className="delete-overlay"
                        position="absolute"
                        inset="-1px"
                        bg="blackAlpha.70"
                        display="none"
                        alignItems="center"
                        justifyContent="center"
                        transition="all 0.2s"
                        onClick={() => removeImage(index)}
                      >
                        <Center>
                          <DeleteIcon width="24px" height="24px" color="white" />
                        </Center>
                      </Box>
                    </Box>
                  ))}
                  {imageCount < maxImageCount && (
                    <FileUpload.Root
                      key={uploadKey}
                      maxFiles={100}
                      accept={['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']}
                      onFileChange={handleFileChange}
                      disabled={isUploading}
                    >
                      <FileUpload.HiddenInput />
                      <FileUpload.Trigger asChild>
                        <Box
                          as="button"
                          w="80px"
                          h="80px"
                          flexShrink={0}
                          borderRadius="md"
                          border="1px dashed"
                          borderColor="border.subtle"
                          bg="grey.64"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          cursor={isUploading ? 'not-allowed' : 'pointer'}
                          transition="all 0.2s"
                          opacity={isUploading ? 0.6 : 1}
                        >
                          <Center color="fg.subtle">
                            {isUploading ? (
                              <Spinner size="sm" color="fg.muted" />
                            ) : (
                              <PlusIcon width="24px" height="24px" />
                            )}
                          </Center>
                        </Box>
                      </FileUpload.Trigger>
                    </FileUpload.Root>
                  )}
                </HStack>
                <Field.ErrorText>{errors?.img?.message as string}</Field.ErrorText>
              </Field.Root>
            </Stack>
          </Stack>
          <Box>
            <Text
              fontWeight="medium"
              fontSize="xs"
              color="fg.muted"
              lineHeight="normal"
              py="2.5"
              mt="2"
            >
              {t('chatSettings.reportNotice', {
                from: from === 'user' ? t('chatSettings.user') : t('chatSettings.group'),
              })}
            </Text>
            <Center>
              <Button
                type="submit"
                rounded="xl"
                minW="162px"
                disabled={disabled}
                loading={loading}
              >
                {t('buttons.confirm')}
              </Button>
            </Center>
          </Box>
        </Flex>
      </form>
    </ContentDrawer>
  );
};

const MemoizedReportCardDrawer = memo(ReportCardDrawer);
export { MemoizedReportCardDrawer as ReportCardDrawer };
